import { useState, useEffect, useCallback } from 'react';
import { notificationService } from 'services/notificationService';
import { Notification, NotificationQuery } from 'types/notifications';

export const useNotifications = (token: string | null) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (query?: NotificationQuery) => {
      if (!token) return;

      setIsLoading(true);
      setError(null);
      try {
        const fetchedNotifications =
          await notificationService.getAllNotifications(token, query);
        setNotifications(fetchedNotifications);
      } catch (err) {
        setError('Failed to fetch notifications');
        console.error('Error fetching notifications:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [token],
  );

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;

    try {
      const count = await notificationService.getUnreadCount(token);
      setUnreadCount(count);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [token]);

  // Mark notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!token) return;

      try {
        await notificationService.markAsRead(token, notificationId);

        // Update local state
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification,
          ),
        );

        // Update unread count
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    },
    [token],
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!token) return;

    try {
      await notificationService.markAllAsRead(token);

      // Update local state
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, read: true })),
      );

      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    if (token) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [token, fetchNotifications, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
};
